// Sidebar: application-level navigation column. Rewritten from dsh's
// ui-sidebar (which is harness/session specific) into a generic nav: brand
// area, menu items with selected state, and a collapse toggle. Collapsed it
// becomes a 56px icon rail.

import type { ReactNode } from 'react'
import clsx from 'clsx'
import { IconPanelLeft } from '../components/icons'
import type { NavItem } from './types'
import css from './Sidebar.module.css'

export type SidebarProps = {
  /** Brand area content (logo + wordmark). */
  brand?: ReactNode
  items: NavItem[]
  /** Id of the currently selected item. */
  activeId: string
  onSelect: (id: string) => void
  /** Icon-rail mode when true. */
  collapsed?: boolean
  /** Renders the collapse toggle when supplied. */
  onToggleCollapse?: () => void
  /** Pinned footer content (theme toggle, account, …). */
  footer?: ReactNode
  /** Accessible name for the nav landmark. */
  ariaLabel?: string
  className?: string
}

/**
 * Render the navigation column.
 * @param props - brand, items, selection, collapse controls and footer.
 * @returns the sidebar element.
 */
export function Sidebar({
  brand,
  items,
  activeId,
  onSelect,
  collapsed = false,
  onToggleCollapse,
  footer,
  ariaLabel = 'Navigation',
  className,
}: SidebarProps) {
  return (
    <aside className={clsx(css.sidebar, collapsed && css.collapsed, className)}>
      <div className={css.brandRow}>
        <span className={css.brand}>{brand}</span>
        {onToggleCollapse != null && (
          <button
            type="button"
            className={css.iconButton}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            onClick={onToggleCollapse}
          >
            <IconPanelLeft size={16} />
          </button>
        )}
      </div>

      <nav className={css.nav} aria-label={ariaLabel}>
        {items.map((item) => {
          const active = item.id === activeId
          return (
            <button
              key={item.id}
              type="button"
              className={clsx(css.item, active && css.active)}
              aria-current={active ? 'page' : undefined}
              title={collapsed ? item.label : undefined}
              onClick={() => { onSelect(item.id) }}
            >
              {item.icon != null && <span className={css.itemIcon}>{item.icon}</span>}
              {!collapsed && <span className={css.itemLabel}>{item.label}</span>}
              {!collapsed && item.badge != null && <span className={css.itemBadge}>{item.badge}</span>}
            </button>
          )
        })}
      </nav>

      {footer != null && <div className={css.footer}>{footer}</div>}
    </aside>
  )
}
